import React, { useState, useEffect, useContext } from "react";
import "./Dashboard.scss";
import Loading from "../../components/Loading/Loading";
import { MdTrendingUp, MdNotifications } from "react-icons/md";
import { FaChurch, FaCalendarAlt, FaUsers, FaMapMarkerAlt, FaChartLine, FaSms, FaEye, FaUserPlus, FaBirthdayCake } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import axios from "axios";
import { api_url } from "../../data";
import { toast } from "react-toastify";

import { Link } from "react-router-dom";
import Header from "../../components/Header";
import DashboardCard from "../../components/DashboardCard";
import { useAuth } from "../../context/Auth";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Income and Expenses Chart Component
const FinancialChart = ({ data }) => {
  const chartData = {
    labels: data?.financial_months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Income',
        data: data?.income_data.map((data) => data.amount) || [5000, 6000, 5500, 7000, 6500, 8000],
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
      },
      {
        label: 'Expenses',
        data: data?.expense_data.map((data) => data.amount) || [3000, 3500, 4000, 4500, 3800, 4200],
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Income vs Expenses'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'GHS' + value.toLocaleString();
          }
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <FaChartLine className="text-blue-600" />
        <span>Financial Statistics</span>
      </h3>
      <div style={{ height: '300px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

// Church Attendance Chart Component
const AttendanceChart = ({ data }) => {
  const chartData = {
    labels: data?.attendance_weeks || ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6'],
    datasets: [
      {
        label: 'Attendance',
        data: data?.weekly_attendance || [85, 92, 78, 95, 88, 102],
        backgroundColor: 'rgba(147, 51, 234, 0.8)',
        borderColor: 'rgba(147, 51, 234, 1)',
        borderWidth: 1,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Weekly Church Attendance'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <FaChurch className="text-purple-600" />
        <span>Church Attendance</span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl">
          <p className="text-3xl font-bold text-purple-600">{data?.last_sunday_attendance || 0}</p>
          <p className="text-sm text-gray-600">Last Sunday</p>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
          <p className="text-3xl font-bold text-blue-600">{data?.average_attendance || 0}</p>
          <p className="text-sm text-gray-600">Monthly Average</p>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
          <p className="text-3xl font-bold text-green-600">{data?.attendance_growth || '+0%'}</p>
          <p className="text-sm text-gray-600">Growth Rate</p>
        </div>
      </div>
      <div style={{ height: '300px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

// Upcoming Events with SMS Component
const UpcomingEvents = ({ data, onSendSMS }) => {
  const [sendingSMS, setSendingSMS] = useState({});

  const handleSendSMS = async (eventId, eventName) => {
    setSendingSMS(prev => ({ ...prev, [eventId]: true }));
    try {
      await onSendSMS(eventId, eventName);
      toast.success(`SMS notifications sent for ${eventName}`);
    } catch (error) {
      toast.error("Failed to send SMS notifications");
    } finally {
      setSendingSMS(prev => ({ ...prev, [eventId]: false }));
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <FaCalendarAlt className="text-orange-600" />
        <span>Upcoming Events</span>
      </h3>
      {data?.upcoming_events_list?.length > 0 ? (
        <div className="space-y-4">
          {data.upcoming_events_list.map((event, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800">{event.name}</h4>
                <p className="text-sm text-gray-600">{event.date} at {event.time}</p>
                <p className="text-xs text-gray-500">{event.location}</p>
              </div>
              <button
                onClick={() => handleSendSMS(event.id, event.name)}
                disabled={sendingSMS[event.id]}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSms />
                <span>{sendingSMS[event.id] ? 'Sending...' : 'Send SMS'}</span>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FaCalendarAlt className="text-4xl mx-auto mb-2 opacity-50" />
          <p>No upcoming events scheduled</p>
        </div>
      )}
    </div>
  );
};

// Upcoming Birthdays Component
const UpcomingBirthdays = ({ data }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
      <FaBirthdayCake className="text-pink-600" />
      <span>Upcoming Birthdays (This Month)</span>
    </h3>
    {data?.upcoming_birthdays?.length > 0 ? (
      <div className="space-y-3">
        {data.upcoming_birthdays.map((birthday, index) => (
          <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-pink-200 rounded-full flex items-center justify-center">
                <FaBirthdayCake className="text-pink-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">{birthday.name}</h4>
                <p className="text-sm text-gray-600">{birthday.date}</p>
                <p className="text-xs text-gray-500">{birthday.age ? `Turning ${birthday.age}` : ''}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-pink-600">{birthday.days_left}</p>
              <p className="text-xs text-gray-500">days left</p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-8 text-gray-500">
        <FaBirthdayCake className="text-4xl mx-auto mb-2 opacity-50" />
        <p>No birthdays this month</p>
      </div>
    )}
    <div className="mt-4 p-4 bg-pink-50 rounded-xl">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Total Birthdays This Month</span>
        <span className="text-xl font-bold text-pink-600">{data?.upcoming_birthdays.length || 0}</span>
      </div>
    </div>
  </div>
);

// Quick Stats Component
const QuickStats = ({ data }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 mt-2 mb-8">
    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
      <FaChartLine className="text-blue-600" />
      <span>Quick Overview</span>
    </h3>
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
      <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
        <FaUsers className="text-blue-600 text-2xl mx-auto mb-2" />
        <p className="text-2xl font-bold text-gray-800">{data?.total_members || 0}</p>
        <p className="text-sm text-gray-600">Total Members</p>
      </div>
      
      <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl">
        <FaCalendarAlt className="text-purple-600 text-2xl mx-auto mb-2" />
        <p className="text-2xl font-bold text-gray-800">{data?.upcoming_events || 0}</p>
        <p className="text-sm text-gray-600">Upcoming Events</p>
      </div>
      <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
        <FaUserPlus className="text-orange-600 text-2xl mx-auto mb-2" />
        <p className="text-2xl font-bold text-gray-800">{data?.today_visitors || 0}</p>
        <p className="text-sm text-gray-600">Today's Visitors</p>
      </div>
      <div className="text-center p-4 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl">
        <FaEye className="text-cyan-600 text-2xl mx-auto mb-2" />
        <p className="text-2xl font-bold text-gray-800">{data?.today_attendance || 0}</p>
        <p className="text-sm text-gray-600">Today's Attendance</p>
      </div>
      <div className="text-center p-4 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl">
        <FaBirthdayCake className="text-pink-600 text-2xl mx-auto mb-2" />
        <p className="text-2xl font-bold text-gray-800">{data?.upcoming_birthdays?.length || 0}</p>
        <p className="text-sm text-gray-600">Birthdays (Month)</p>
      </div>
    </div>
  </div>
);

// Main Dashboard Component
const Dashboard = () => {
  const { requestWithAuth } = useAuth();
  const [gettingData, setGettingData] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  // GET DASHBOARD DATA
  const getDashboardData = async () => {
    setGettingData(true);
    try {
      const response = await requestWithAuth("/accounts/dashboard-data/", "GET");
      setDashboardData(response);
    } catch (error) {
      toast.error("An error occurred while getting data");
    } finally {
      setGettingData(false);
    }
  };

  // SEND SMS NOTIFICATIONS FOR EVENTS
  const sendEventSMS = async (eventId, eventName) => {
    try {
      const response = await requestWithAuth("/events/send-sms-notification/", "POST", {
        event_id: eventId,
        message: `Reminder: ${eventName} is happening today! Don't miss it.`
      });
      return response;
    } catch (error) {
      throw new Error("Failed to send SMS notifications");
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  if (gettingData && !dashboardData) {
    return <Loading />;
  }

  return (
    <div>
      <Header title="Dashboard" />
      
      {/* Main Dashboard Cards */}
      <div className="grid lg:grid-cols-3 lg:gap-8 mb-8">
        <DashboardCard
          title="Total Members"
          subtitle="Overall"
          value={dashboardData?.total_members || 0}
          icon={FaChurch}
          trendValue="+12%"
          color="blue"
        />

        <DashboardCard
          title="New Members"
          subtitle="This Month"
          value={dashboardData?.new_members || 0}
          icon={FaUsers}
          trendValue="+12%"
          color="blue"
        />

        <DashboardCard
          title="Net Income"
          subtitle="This Month"
          value={dashboardData?.total_balance || 0}
          icon={FaChartLine}
          trendValue="+12%"
          color="green"
        />
      </div>

      {/* Quick Stats Overview */}
      <QuickStats data={dashboardData} />

      {/* Charts Section - Side by Side */}
      <div className="">
        <FinancialChart data={dashboardData} />
        {/* <AttendanceChart data={dashboardData} /> */}
      </div>

      {/* Events and Birthdays Section - Side by Side */}
      <div className="grid lg:grid-cols-2 lg:gap-8">
        <UpcomingEvents data={dashboardData} onSendSMS={sendEventSMS} />
        <UpcomingBirthdays data={dashboardData} />
      </div>
    </div>
  );
};

export default Dashboard;